import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { ensureUserAccount } from '@/services/accountService';
import { getBillingStatus, createPortalSession } from '@/services/billingService';
import { Button } from '@/components/ui/button';

/**
 * BillingBanner - Displays trial countdown and billing state warnings
 * 
 * Shows different banners based on subscription status:
 * - Trialing: "Free trial ends in X days" with manage billing CTA
 * - Past due/Unpaid: Warning with manage billing CTA
 * - Canceled: Reactivation prompt with manage billing CTA
 */
export default function BillingBanner() {
  const { user } = useAuth();
  const [account, setAccount] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  const loadBillingData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const result = await ensureUserAccount(user);
      setAccount(result?.account);

      if (result?.account?.id) {
        const status = await getBillingStatus(result.account.id);
        setBillingStatus(status);
      }
    } catch (err) {
      console.error('[BillingBanner] Error loading billing data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  const handleManageBilling = async () => {
    if (!account || !user) {
      console.error('[BillingBanner] Cannot open portal - missing account or user');
      return;
    }

    try {
      setPortalLoading(true);
      console.log('[BillingBanner] Opening portal for account:', account.id);
      const result = await createPortalSession(account.id, user.email, account.name);
      console.log('[BillingBanner] Portal result:', result);
      
      if (result?.url) {
        console.log('[BillingBanner] Redirecting to:', result.url);
        window.location.assign(result.url);
      } else {
        console.error('[BillingBanner] No URL in response');
        setPortalLoading(false);
      }
    } catch (err) {
      console.error('[BillingBanner] Error opening portal:', err);
      setPortalLoading(false);
    }
    // Note: Don't set portalLoading to false on success - we're navigating away
  };

  // Don't show anything while loading or if no billing data
  if (loading || !billingStatus) {
    return null;
  }

  const { status, trialEnd, trialDaysRemaining } = billingStatus;

  // Format trial end date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Trial banner
  if (status === 'trialing' && trialEnd) {
    const daysText = trialDaysRemaining === 1 ? 'day' : 'days';
    const endDate = formatDate(trialEnd);

    return (
      <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4 text-blue-950 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-100 text-blue-700">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <div>
              <p className="text-sm font-semibold">
                {trialDaysRemaining > 0 ? (
                  <>Free trial ends in <strong>{trialDaysRemaining} {daysText}</strong> (on {endDate})</>
                ) : (
                  <>Your free trial ends <strong>today</strong></>
                )}
              </p>
              <p className="mt-1 text-sm text-blue-800">
                Add a payment method now to keep quoting without interruption.
              </p>
            </div>
          </div>
          <Button
            onClick={handleManageBilling}
            disabled={portalLoading}
            variant="outline"
            size="sm"
            className="shrink-0 border-blue-200 bg-white text-blue-700 hover:bg-blue-100 hover:text-blue-800"
          >
            {portalLoading ? 'Loading...' : 'Add Payment Method'}
          </Button>
        </div>
      </div>
    );
  }

  // Past due / Unpaid banner
  if (status === 'past_due' || status === 'unpaid') {
    return (
      <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-red-950 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-red-100 text-red-700">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </span>
            <div>
              <p className="text-sm font-semibold">Payment issue detected</p>
              <p className="mt-1 text-sm text-red-800">Update your payment method to keep access.</p>
            </div>
          </div>
          <Button
            onClick={handleManageBilling}
            disabled={portalLoading}
            variant="outline"
            size="sm"
            className="shrink-0 border-red-200 bg-white text-red-700 hover:bg-red-100 hover:text-red-800"
          >
            {portalLoading ? 'Loading...' : 'Update Payment Method'}
          </Button>
        </div>
      </div>
    );
  }

  // Canceled banner
  if (status === 'canceled') {
    return (
      <div className="mb-6 rounded-md border border-slate-200 bg-white p-4 text-slate-950 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-700">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </span>
            <div>
              <p className="text-sm font-semibold">Subscription canceled</p>
              <p className="mt-1 text-sm text-slate-600">Reactivate to continue using GreenQuote.</p>
            </div>
          </div>
          <Button
            onClick={handleManageBilling}
            disabled={portalLoading}
            variant="outline"
            size="sm"
            className="shrink-0"
          >
            {portalLoading ? 'Loading...' : 'Reactivate Subscription'}
          </Button>
        </div>
      </div>
    );
  }

  // Active subscription - no banner needed (or could show a subtle "Pro" badge)
  return null;
}
